import { Link, useSearchParams } from 'react-router-dom';
import { Boxes, CalendarDays, Columns3, List, Settings, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SERVICE_REQUEST_VIEWS, type ServiceRequestView } from '../../lib/service-request-views';
import { useSession } from '../session-context';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export const SERVICE_REQUEST_VIEW_ICONS: Record<ServiceRequestView, LucideIcon> = {
  board: Columns3,
  list: List,
  calendar: CalendarDays,
  implementations: Boxes,
};

/** Board · List · Calendar · Implementations strip shared by the SR pages. */
export default function ServiceRequestNav({ active }: { active: ServiceRequestView }) {
  const { isOrgAdmin } = useSession();
  const [searchParams] = useSearchParams();

  /** Board and list are the same query seen two ways, so switching keeps the filters. */
  function linkTo(view: ServiceRequestView, fallback: string): string {
    if (view !== 'board' && view !== 'list') return fallback;
    const params = new URLSearchParams(searchParams);
    params.delete('peek');
    if (view === 'list') params.set('layout', 'list');
    else params.delete('layout');
    const query = params.toString();
    return query ? `/service-requests?${query}` : '/service-requests';
  }

  return (
    <nav aria-label="Service Request views" className="-mb-1 flex flex-wrap items-center gap-1 border-b">
      {SERVICE_REQUEST_VIEWS.map((view) => {
        const isActive = view.id === active;
        const Icon = SERVICE_REQUEST_VIEW_ICONS[view.id];
        return (
          <Link
            key={view.id}
            to={linkTo(view.id, view.to)}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              '-mb-px flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-sm transition-colors hover:no-underline',
              isActive
                ? 'border-primary font-medium text-primary'
                : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {view.label}
          </Link>
        );
      })}
      {isOrgAdmin ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to="/settings/service-requests"
              aria-label="Service Request settings"
              className="-mb-px ml-auto flex items-center gap-1.5 border-b-2 border-transparent px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground hover:no-underline"
            >
              <Settings className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Settings</span>
            </Link>
          </TooltipTrigger>
          <TooltipContent>Request types, approval roles, readiness checklists and SLA</TooltipContent>
        </Tooltip>
      ) : null}
    </nav>
  );
}
